import React, { useEffect, useState } from 'react';
import exifr from 'exifr';
import {
    Box,
    Container,
    Typography,
    useTheme,
    Stack,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid2,
    Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import useResponsive from '../hooks/useResponsive';
import useGeolocation from '../hooks/useGeolocation';
import { FaunaSpecies } from '../apiService/ObservationApiService';
import observationApiService from '../apiService/ObservationApiService';
import { ObservationData } from '../interfaces/Observation';
import { useTranslation } from 'react-i18next';
import { useAlert } from '../contexts/AlertContext';
import ObservationForm, { WeatherData } from '../components/Observation/ObservationForm';
import { useDropdownData } from '../hooks/useDropdownData';
import MediaUploadSection from '../components/Observation/MediaUploadSection';
import MediaSelectionPopup, { MediaType } from '../components/Observation/MediaSelectionPopup';
import MediaPreviewModal from '../components/Observation/MediaPreviewModal';
import AudioRecorderModal from '../components/Observation/AudioRecorderModal';
import { useOfflineSync } from '../hooks/useOfflineSync';

// Import custom SVG icons
import ExpandIcon from '../assets/Expand.svg?react';
import CollapseIcon from '../assets/collapse.svg?react';
import CommonButton from '../components/CommonButton';
import dropdownApiService from '../apiService/DropdownApiService';
import CommonLoader from '../components/CommonLoader';
import LocalStorageUtil from '../utils/localStorageUtil';
import { storageKeys } from '../utils/constants';
import { parseEWKBHexToCoords } from '../utils/commonMethods';
import { BuildingInfo } from '../interfaces/Address';
import { AppName, getAppName } from '../utils/commonMethods';
import { renderSpeciesIconInverted } from '../utils/iconHelper';

const ObservationScreen: React.FC = () => {
    // Loading state for edit observation fetch
    const [editLoading, setEditLoading] = useState(false);
    // Store fauna species groups for icon lookup
    const [faunaSpeciesGroups, setFaunaSpeciesGroups] = useState<any[]>([]);
    // Store observation data for edit mode
    const [editObservationData, setEditObservationData] = useState<any>(null);
    // Address building info captured from reverse-geocoding (create mode only)
    const [locationBuildingInfo, setLocationBuildingInfo] = useState<BuildingInfo | null>(null);

    // Get fauna species groups from Redux instead of fetching
    const { speciesGroupData } = useDropdownData();
    const { getLocationForObservation } = useGeolocation();

    // Set fauna species groups from Redux data on mount
    useEffect(() => {
        if (speciesGroupData && speciesGroupData.length > 0) {
            setFaunaSpeciesGroups(speciesGroupData);
        } else if (!navigator.onLine) {
            const fallbackGroups = dropdownApiService.getFallbackFaunaSpeciesGroups();
            setFaunaSpeciesGroups(fallbackGroups);
        } else {
            const fetchGroups = async () => {
                const groups = await dropdownApiService.getFaunaSpeciesGroups();
                setFaunaSpeciesGroups(groups || []);
            };
            fetchGroups();
        }
    }, [speciesGroupData]);

    // Helper to get correct icon ID for create/edit mode
    const getSpeciesIconId = (): string => {
        // Get group/subgroup from selectedSpecies (create) or editObservationData (edit)
        let groupId = selectedSpecies?.species_group_id;
        let subgroupId = selectedSpecies?.species_subgroup_id;

        // For edit mode, use the loaded observation data
        if (observationId && editObservationData) {
            groupId = editObservationData.species_group_id;
            subgroupId = editObservationData.species_subgroup_id;
        }

        // Birds (group 10)
        if (groupId === 10) {
            return 'birds';
        }
        // Bats (group 11, subgroup 30)
        if (groupId === 11 && subgroupId === 30) {
            return 'bats';
        }
        // Mammals (group 11, not subgroup 30)
        if (groupId === 11 && subgroupId !== 30) {
            return 'mammals';
        }
        // Others (groups 1-9)
        if (typeof groupId === 'number' && groupId >= 1 && groupId <= 9) {
            return 'others';
        }
        // Default to others
        return 'others';
    };

    // Preview modal and audio recorder state hooks
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewMediaItem, setPreviewMediaItem] = useState<any>(null);
    const [previewMediaType, setPreviewMediaType] = useState<MediaType>('image');
    const [audioRecorderOpen, setAudioRecorderOpen] = useState(false);
    // Media state hooks (fix missing state errors)
    const [currentMediaType, setCurrentMediaType] = useState<MediaType>('image');
    const [imageItems, setImageItems] = useState<any[]>([]);
    const [videoItems, setVideoItems] = useState<any[]>([]);
    const [audioItems, setAudioItems] = useState<any[]>([]);

    // Weather data state
    const [apiWeatherData, setApiWeatherData] = useState<WeatherData | null>(null);

    const [manualWeatherData, setManualWeatherData] = useState<WeatherData | null>(null);

    // Handler for manual weather data changes
    const handleManualWeatherChange = (data: Partial<WeatherData>) => {
        // Update both the form fields and the state for consistency
        if (data.api_temperature !== undefined) setValue('manual_temperature', data.api_temperature);
        if (data.api_wind_speed !== undefined) setValue('manual_wind_speed', data.api_wind_speed);
        if (data.api_wind_direction !== undefined) setValue('manual_wind_direction', data.api_wind_direction);
        if (data.api_weather_code !== undefined) setValue('manual_weather_code', data.api_weather_code);

        setManualWeatherData(prev => ({
            api_temperature: data.api_temperature !== undefined ? data.api_temperature : (prev?.api_temperature ?? null),
            api_wind_speed: data.api_wind_speed !== undefined ? data.api_wind_speed : (prev?.api_wind_speed ?? null),
            api_wind_direction: data.api_wind_direction !== undefined ? data.api_wind_direction : (prev?.api_wind_direction ?? null),
            api_weather_code: data.api_weather_code !== undefined ? data.api_weather_code : (prev?.api_weather_code ?? null),
        }));
    };

    // Removed edit observation logic
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const { id: observationId } = useParams();
    const { isMobile, isTablet, isDesktop } = useResponsive();
    // Loading state for API call
    const [isSaving, setIsSaving] = useState(false);

    // Initialize offline sync functionality
    const { isOnline } = useOfflineSync();

    // Get selected species from navigation state
    const selectedSpecies = location.state?.species as FaunaSpecies;
    const mapLocation = location.state?.location as { lat: number; lng: number };



    // Get dropdown data based on selected species
    const { fetchDropdownData, getDropdownDataForSpeciesGroup, globalData } = useDropdownData();
    const [currentDropdownData, setCurrentDropdownData] = useState<any>(null);
    const [dropdownLoading, setDropdownLoading] = useState(false);

    const [soortinformatieExpanded, setSoortinformatieExpanded] = useState(false);

    // Media selection popup state
    const [mediaPopupOpen, setMediaPopupOpen] = useState(false);
    const portalProjectId = LocalStorageUtil.getItem(storageKeys.selectedProjectIdForNavigation, null);
    const isBio = getAppName() === AppName.BioEcologie;
    const brandMain = isBio ? theme.palette.primary.main : (theme.palette.blueColors.main ?? theme.palette.primary.main);

    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
    } = useForm<ObservationData, any, ObservationData>({
        mode: 'onBlur',
        reValidateMode: 'onChange',
        defaultValues: {
            date: new Date().toISOString().split('T')[0],
            time: new Date().toTimeString().slice(0, 5),
            geom: '',
            observer: typeof window !== 'undefined' ? Number(localStorage.getItem('workerId')) || 0 : 0,
            address: '',
            mapnr: null,
            species_id: 0,
            quantity: '',
            count_method_id: 0,
            ndff_stage_id: 0,
            behaviour_id: 0,
            trace_id: 0,
            residence_id: 0,
            residence_loc_id: 0,
            ndff_gender_id: 0,
            ndff_determined: '',
            ndff_counted: '',
            not_sure: false,
            ndff_cod_id: 0,
            remark: '',
            manual_temperature: null,
            manual_wind_speed: null,
            manual_wind_direction: null,
            manual_weather_code: null,
        },
    });

    useEffect(() => {
        // If no species selected, redirect back
        if (!selectedSpecies && !observationId) {
            navigate(-1);
        }
    }, [selectedSpecies, observationId, navigate]);

    // Fetch weather data when location is available
    useEffect(() => {
        // Skip fetching fresh weather data in edit mode - use stored data instead
        if (observationId) {
            return;
        }

        console.log("map location", mapLocation)
        console.log("editObservationData", editObservationData)

        const fetchWeatherData = async () => {
            let lat: number | null = null;
            let lng: number | null = null;

            // For new observations, use mapLocation
            if (mapLocation?.lat && mapLocation?.lng) {
                lat = mapLocation.lat;
                lng = mapLocation.lng;
            }
            // For edit mode, extract coordinates from geom field
            else if (editObservationData?.geom) {
                try {
                    const coords = parseEWKBHexToCoords(editObservationData?.geom);
                    if (coords) {
                        console.log("lng", lng)
                        console.log("lat", lat)
                        lat = coords.lat
                        lng = coords.lng
                    }
                } catch {
                    console.log("Error in parsing goem")
                }
            }

            if (lat && lng) {
                try {
                    const response = await observationApiService.fetchWeatherData(
                        lat.toString(),
                        lng.toString()
                    );
                    console.log("map location response", response)

                    if (response && response.data) {
                        // Parse the string values to numbers and convert comma to dot for decimals
                        const parseWeatherValue = (value: string): number | null => {
                            if (!value) return null;
                            const normalized = value.replace(',', '.');
                            const parsed = parseFloat(normalized);
                            return isNaN(parsed) ? null : parsed;
                        };

                        const apiData = {
                            api_temperature: parseWeatherValue(response.data.api_temperature),
                            api_wind_speed: parseWeatherValue(response.data.api_wind_speed),
                            api_wind_direction: response.data.api_wind_direction || null,
                            api_weather_code: response.data.api_weather_code || null,
                        };
                        setApiWeatherData(apiData);
                        // Also set form values
                        setValue('api_temperature', apiData.api_temperature);
                        setValue('api_wind_speed', apiData.api_wind_speed);
                        setValue('api_wind_direction', apiData.api_wind_direction);
                        setValue('api_weather_code', apiData.api_weather_code);
                    }
                } catch (error) {
                    console.error('Error fetching weather data:', error);
                }
            }
        };

        fetchWeatherData();
    }, [mapLocation, editObservationData?.geom]);

    // Load manual weather data from navigation state or stored project data
    useEffect(() => {
        // Skip in edit mode - use API response instead
        if (observationId) {
            return;
        }

        try {
            // First try to get manual weather data from navigation state
            const navManualWeather = location.state?.manualWeatherData;
            let weatherSource = navManualWeather;

            // Fallback to localStorage if not available in navigation state
            if (!weatherSource) {
                const storedProjectData = localStorage.getItem('currentProjectData');
                if (storedProjectData) {
                    const projectData = JSON.parse(storedProjectData);
                    weatherSource = projectData.manual_weather_data;
                }
            }

            if (weatherSource) {
                const parseWeatherValue = (value: string): number | null => {
                    if (!value) return null;
                    const normalized = value.replace(',', '.');
                    const parsed = parseFloat(normalized);
                    return isNaN(parsed) ? null : parsed;
                };

                const manualData = {
                    api_temperature: parseWeatherValue(weatherSource.manual_temperature),
                    api_wind_speed: parseWeatherValue(weatherSource.manual_wind_speed),
                    api_wind_direction: weatherSource.manual_wind_direction || null,
                    api_weather_code: weatherSource.manual_weather_code || null,
                };
                setManualWeatherData(manualData);
                // Also set form values
                setValue('manual_temperature', manualData.api_temperature);
                setValue('manual_wind_speed', manualData.api_wind_speed);
                setValue('manual_wind_direction', manualData.api_wind_direction);
                setValue('manual_weather_code', manualData.api_weather_code);
            }
        } catch (error) {
            console.error('Error loading manual weather data:', error);
        }
    }, []);

    // Fetch dropdown data when species is available (either from selectedSpecies or editObservationData)
    useEffect(() => {
        const loadDropdownData = async () => {
            // Get species group info from either selectedSpecies (create mode) or editObservationData (edit mode)
            const speciesGroupId = selectedSpecies?.species_group_id || editObservationData?.species_group_id;
            const speciesSubgroupId = selectedSpecies?.species_subgroup_id || editObservationData?.species_subgroup_id;

            if (speciesGroupId) {
                setDropdownLoading(true);
                try {
                    const data = await fetchDropdownData(
                        speciesGroupId.toString(),
                        speciesSubgroupId?.toString()
                    );
                    setCurrentDropdownData(data);
                } catch (error) {
                    console.error('Error loading dropdown data:', error);
                } finally {
                    setDropdownLoading(false);
                }
            }
        };

        loadDropdownData();
    }, [selectedSpecies, editObservationData, fetchDropdownData, observationId]);

    // Fetch observation by ID for edit workflow
    useEffect(() => {
        // Get observationId from route params
        if (!observationId) return;

        const fetchObservationById = async () => {
            setEditLoading(true);
            try {
                const response = await observationApiService.getObservationById(observationId);
                if (response && response.status_code === 1 && response.data && response.data.observation) {
                    const obs = response.data.observation;
                    // Extract all needed fields
                    console.log("address edit ***", obs.building_address)
                    const observationData = {
                        geom: obs.geom ?? '',
                        date: obs.date ?? '',
                        time: obs.time ?? '',
                        observer: obs.observer ?? 0,
                        address: obs.building_address ?? '',
                        mapnr: obs.mapnr ?? null,
                        species_id: obs.species_id ?? 0,
                        quantity: obs.quantity ? obs.quantity.toString() : '',
                        not_sure: obs.not_sure ?? false,
                        count_method_id: obs.count_method_id ?? 0,
                        ndff_stage_id: obs.ndff_stage_id ?? 0,
                        behaviour_id: obs.behaviour_id ?? 0,
                        trace_id: obs.trace_id ?? 0,
                        residence_id: obs.residence_id ?? 0,
                        residence_loc_id: obs.residence_loc_id ?? 0,
                        ndff_gender_id: obs.ndff_gender_id ?? 0,
                        ndff_determined: obs.ndff_determined ?? '',
                        ndff_counted: obs.ndff_counted ?? '',
                        ndff_cod_id: obs.ndff_cod_id ?? 0,
                        remark: obs.remark ?? '',
                        is_flexible: obs.is_flexible ?? 0
                    };
                    // Prefill the form
                    reset(observationData);
                    // Store observation data for icon logic
                    const editData = {
                        species_group_id: obs.species_group_id,
                        species_subgroup_id: obs.species_subgroup_id,
                        geom: obs.geom,
                        observer_name: obs.observer_name,
                        address: obs.building_address ?? ''
                    };

                    setEditObservationData(editData);

                    // Load weather data from API response (both API and manual)
                    if (obs.api_temperature !== undefined || obs.api_weather_code !== undefined ||
                        obs.api_wind_speed !== undefined || obs.api_wind_direction !== undefined) {
                        const apiData = {
                            api_temperature: obs.api_temperature ?? null,
                            api_wind_speed: obs.api_wind_speed ?? null,
                            api_wind_direction: obs.api_wind_direction ?? null,
                            api_weather_code: obs.api_weather_code ?? null,
                        };
                        setApiWeatherData(apiData);
                        // Also set form values
                        setValue('api_temperature', apiData.api_temperature);
                        setValue('api_wind_speed', apiData.api_wind_speed);
                        setValue('api_wind_direction', apiData.api_wind_direction);
                        setValue('api_weather_code', apiData.api_weather_code);
                    }

                    if (obs.manual_temperature !== undefined || obs.manual_weather_code !== undefined ||
                        obs.manual_wind_speed !== undefined || obs.manual_wind_direction !== undefined) {
                        const manualData = {
                            api_temperature: obs.manual_temperature ?? null,
                            api_wind_speed: obs.manual_wind_speed ?? null,
                            api_wind_direction: obs.manual_wind_direction ?? null,
                            api_weather_code: obs.manual_weather_code ?? null,
                        };
                        setManualWeatherData(manualData);
                        // Also set form values
                        setValue('manual_temperature', manualData.api_temperature);
                        setValue('manual_wind_speed', manualData.api_wind_speed);
                        setValue('manual_wind_direction', manualData.api_wind_direction);
                        setValue('manual_weather_code', manualData.api_weather_code);
                    }

                    // Prefill photos and media
                    let baseUrl = import.meta.env.VITE_APP_BASEURL || '';
                    baseUrl = baseUrl.replace(/\/api$/, '');
                    const makeAbsoluteUrl = (url: string) => url.startsWith('http') ? url : `${baseUrl}${url}`;
                    if (response.data.photos && Array.isArray(response.data.photos)) {

                        setImageItems(response.data.photos.map((photo: any) => {
                            const filePath = photo.file_path || '';

                            // let path = '';
                            // if (portalProjectId && observationId) {
                            //     path = `/storage/projects/${portalProjectId}/fieldapp/images/${observationId}/${filePath}`;
                            // }
                            const absolute = photo.url ?? '';
                            return {
                                id: `photo_${photo.photo_id}`,
                                type: 'upload',
                                name: (filePath || '').split('/').pop(),
                                original_file_name: photo.original_file_name,
                                thumbnail: absolute,
                                file: null,
                                photo_id: photo.photo_id,
                                file_path: photo.file_path,
                                file_name: photo.file_name || (photo.file_path || '').split('/').pop(),
                                obs_species_id: photo.obs_species_id || observationId,
                                project_id: photo.project_id || null,
                                direction: photo.direction,
                                taken_at: photo.taken_at,
                                isExisting: true,
                                toDelete: false
                            };
                        }));
                    }
                    if (response.data.media && Array.isArray(response.data.media)) {
                        // Video items
                        const videoItems = response.data.media.filter((m: any) => m.type === 'video').map((media: any) => {
                            let videoPath = media.media_url;
                            let thumbnailPath = media.thumbnail_url;
                            // if (portalProjectId && observationId) {
                            //     videoPath = `/storage/projects/${portalProjectId}/fieldapp/video/${observationId}/${media.file_path}`;
                            //     if (media.thumbnail) {
                            //         thumbnailPath = `/storage/projects/${portalProjectId}/fieldapp/video/${observationId}/thumbnails/${media.thumbnail.replace(/^thumbnails\//, '')}`;
                            //     } else {
                            //         thumbnailPath = videoPath; // fallback
                            //     }
                            // }
                            return {
                                id: `media_${media.media_id}`,
                                uiType: 'upload',
                                name: media.file_path.split('/').pop(),
                                original_file_name: media.original_file_name,
                                thumbnail: thumbnailPath ? makeAbsoluteUrl(thumbnailPath) : '',
                                videoUrl: videoPath ? makeAbsoluteUrl(videoPath) : '',
                                file: null,
                                media_id: media.media_id,
                                file_path: media.file_path,
                                file_name: media.file_name || media.file_path.split('/').pop(),
                                obs_species_id: media.obs_species_id || observationId,
                                type: media.type,
                                taken_at: media.taken_at,
                                isExisting: true,
                                toDelete: false
                            };
                        });
                        setVideoItems(videoItems);
                        // Audio items
                        const audioItems = response.data.media.filter((m: any) => m.type === 'audio').map((media: any) => {
                            let audioPath = media.media_url;;
                            // if (portalProjectId && observationId) {
                            //     audioPath = `/storage/projects/${portalProjectId}/fieldapp/audio/${observationId}/${media.file_path}`;
                            // }
                            return {
                                id: `media_${media.media_id}`,
                                uiType: 'upload',
                                name: media.file_path.split('/').pop(),
                                original_file_name: media.original_file_name,
                                thumbnail: audioPath ? makeAbsoluteUrl(audioPath) : '',
                                audioUrl: audioPath ? makeAbsoluteUrl(audioPath) : '',
                                file: null,
                                media_id: media.media_id,
                                file_path: media.file_path,
                                file_name: media.file_name || media.file_path.split('/').pop(),
                                obs_species_id: media.obs_species_id || observationId,
                                type: media.type,
                                taken_at: media.taken_at,
                                isExisting: true,
                                toDelete: false
                            };
                        });
                        setAudioItems(audioItems);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch observation by ID:', error);
            } finally {
                setEditLoading(false);
            }
        };
        fetchObservationById();
    }, [reset, fetchDropdownData]);


    // Removed edit observation fetching effect

    const handleBack = () => {
        navigate(-1);
    };

    const handleSoortinformatieToggle = () => {
        setSoortinformatieExpanded(!soortinformatieExpanded);
    };

    // Helper function to calculate total size of all media files
    const calculateTotalMediaSize = (): number => {
        let totalSize = 0;
        imageItems.forEach(item => {
            if (item.file) totalSize += item.file.size;
        });
        videoItems.forEach(item => {
            if (item.file) totalSize += item.file.size;
        });
        audioItems.forEach(item => {
            if (item.file) totalSize += item.file.size;
        });
        return totalSize;
    };

    // Helper function to format bytes to MB
    const formatBytesToMB = (bytes: number): string => {
        return (bytes / (1024 * 1024)).toFixed(2);
    };

    const validateFileSize = (file: File): boolean => {
        const environment = import.meta.env.VITE_APP_ENVIRONMENT || 'test';
        let maxSizeMB: number | undefined = undefined;
        if (environment === 'test') {
            maxSizeMB = 20;
        } else if (environment === 'uat') {
            maxSizeMB = 50;
        }
        if (maxSizeMB !== undefined) {
            const maxSizeBytes = maxSizeMB * 1024 * 1024;
            // Check individual file size OR total accumulated size
            if (file.size > maxSizeBytes || (calculateTotalMediaSize() + file.size) > maxSizeBytes) {
                showAlert(
                    t('observation.fileSizeExceeded', { maxSizeMB }),
                    'error'
                );
                return false;
            }
        }
        return true;
    };

    // Function to generate video thumbnail (with HEVC fallback support)
    const generateVideoThumbnail = (file: File): Promise<{ url: string; file: File | null; isHevc: boolean }> => {
        return new Promise((resolve, reject) => {
            // Check if file is HEVC
            const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
            const isHevc = ext === '.hevc';

            // For HEVC files, generate a placeholder thumbnail since browsers can't decode HEVC
            if (isHevc) {
                try {
                    // Create a canvas with HEVC video icon/placeholder
                    const canvas = document.createElement('canvas');
                    canvas.width = 200;
                    canvas.height = 200;
                    const context = canvas.getContext('2d');

                    if (!context) {
                        reject(new Error('Canvas context not available'));
                        return;
                    }

                    // Draw background
                    context.fillStyle = '#1a1a1a';
                    context.fillRect(0, 0, canvas.width, canvas.height);

                    // Draw play button icon (to indicate video)
                    context.fillStyle = '#ffffff';
                    context.beginPath();
                    context.moveTo(80, 70);
                    context.lineTo(80, 130);
                    context.lineTo(130, 100);
                    context.closePath();
                    context.fill();

                    // Draw HEVC text
                    context.font = 'bold 14px Arial';
                    context.fillStyle = '#ffaa00';
                    context.textAlign = 'center';
                    context.fillText('HEVC', 100, 160);

                    // Convert canvas to blob
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const thumbnailUrl = URL.createObjectURL(blob);
                            resolve({ url: thumbnailUrl, file: null, isHevc: true });
                        } else {
                            reject(new Error('Failed to generate HEVC thumbnail'));
                        }
                    }, 'image/jpeg', 0.8);
                } catch (error) {
                    reject(new Error('Failed to create HEVC placeholder thumbnail'));
                }
                return;
            }

            // For other video formats, generate actual thumbnail from video frame
            const video = document.createElement('video');
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            video.addEventListener('loadedmetadata', () => {
                // Set canvas dimensions to match video (or fixed size)
                canvas.width = 200;
                canvas.height = 200;

                // Seek to 1 second or 10% of video duration for thumbnail
                video.currentTime = Math.min(1, video.duration * 0.1);
            });

            video.addEventListener('seeked', () => {
                if (context) {
                    // Draw video frame to canvas
                    context.drawImage(video, 0, 0, canvas.width, canvas.height);

                    // Convert canvas to blob and create both URL and File
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const thumbnailUrl = URL.createObjectURL(blob);
                            // Create File object from blob for upload
                            const thumbnailFile = new File([blob], `thumbnail_${Date.now()}.jpg`, { type: 'image/jpeg' });
                            resolve({ url: thumbnailUrl, file: thumbnailFile, isHevc: false });
                        } else {
                            reject(new Error('Failed to generate thumbnail'));
                        }
                    }, 'image/jpeg', 0.8);
                } else {
                    reject(new Error('Canvas context not available'));
                }

                // Clean up
                video.remove();
            });

            video.addEventListener('error', () => {
                reject(new Error('Video loading failed'));
            });

            // Load video file
            video.src = URL.createObjectURL(file);
            video.load();
        });
    };

    // Helper function to extract EXIF data and add image item
    const addImageWithExifData = async (file: File) => {
        try {
            await addImageWithExifAndLocationData(file, null);
        } catch (error) {
            console.error('Image processing failed:', error);

            let errorMessage = 'Failed to add image. Please try again.';

            if (error instanceof Error) {
                errorMessage = `Image processing error: ${error.message}`;
            }

            showAlert(errorMessage, 'error');
        }
    };

    // Enhanced helper function that combines EXIF data with Geolocation API coordinates
    const addImageWithExifAndLocationData = async (file: File, locationCoords: any) => {
        let processedFile = file;
        const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        const isHeic = ext === '.heic' || ext === '.heif';
        if (isHeic) {
            showAlert(t('observation.heicConvertedToJpg'), 'info');
            try {
                if (typeof (window as any).heic2any === 'function') {
                    const convertedBlob = await (window as any).heic2any({
                        blob: file,
                        toType: 'image/jpeg',
                        quality: 0.95
                    });
                    const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
                    const originalName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
                    processedFile = new File([blob], originalName, { type: 'image/jpeg' });
                } else {
                    console.warn('Uploading HEIC/HEIF failed');
                }
            } catch (error) {
                console.warn('HEIC/HEIF conversion failed, uploading original file:', error);
            }
        }

        const imageUrl = URL.createObjectURL(processedFile);
        let gpsCoords = null;
        let exifData = null;

        try {
            // Try to extract GPS coordinates from EXIF data
            const exifParser = exifr;

            if (exifParser) {
                // Parse ALL EXIF data from the processed file (JPEG if converted)
                const allExifData = await exifParser.parse(processedFile);

                // Then try specific GPS parsing with different approaches
                try {
                    exifData = await exifParser.parse(processedFile, {
                        gps: true,
                        tiff: true,
                        exif: true,
                        pick: ['GPS', 'GPSLatitude', 'GPSLongitude', 'GPSLatitudeRef', 'GPSLongitudeRef',
                            'GPSImgDirection', 'GPSAltitude', 'DateTime', 'DateTimeOriginal', 'CreateDate', 'Orientation']
                    });
                } catch (parseError) {
                    console.warn('Specific GPS parsing failed, trying basic parse:', parseError);
                    exifData = await exifParser.parse(processedFile, ['GPS', 'DateTime', 'DateTimeOriginal', 'CreateDate', 'Orientation']);
                }

                // Check for GPS coordinates in multiple formats
                let foundGPS = false;

                // Check allExifData first for direct latitude/longitude fields (most reliable)
                if (allExifData && allExifData.latitude && allExifData.longitude) {
                    gpsCoords = {
                        lat: allExifData.latitude,
                        lng: allExifData.longitude,
                        source: 'exif_direct_all'
                    };
                    foundGPS = true;
                }

                // Direct latitude/longitude fields in specific exifData
                if (!foundGPS && exifData && exifData.latitude && exifData.longitude) {
                    gpsCoords = {
                        lat: exifData.latitude,
                        lng: exifData.longitude,
                        source: 'exif_direct_specific'
                    };
                    foundGPS = true;
                }

                // GPS fields with references
                if (!foundGPS && exifData && exifData.GPSLatitude && exifData.GPSLongitude) {
                    let lat = exifData.GPSLatitude;
                    let lng = exifData.GPSLongitude;

                    // Convert DMS to decimal if needed
                    if (Array.isArray(lat) && lat.length === 3) {
                        lat = lat[0] + (lat[1] / 60) + (lat[2] / 3600);
                    }
                    if (Array.isArray(lng) && lng.length === 3) {
                        lng = lng[0] + (lng[1] / 60) + (lng[2] / 3600);
                    }

                    // Apply hemisphere corrections
                    if (exifData.GPSLatitudeRef === 'S') lat = -lat;
                    if (exifData.GPSLongitudeRef === 'W') lng = -lng;

                    gpsCoords = {
                        lat: lat,
                        lng: lng,
                        source: 'exif_gps_fields'
                    };
                    foundGPS = true;
                }

                // Comprehensive search through all GPS-related fields in allExifData
                if (!foundGPS && allExifData) {
                    const gpsKeys = Object.keys(allExifData).filter(key =>
                        key.toLowerCase().includes('gps') ||
                        key.toLowerCase().includes('lat') ||
                        key.toLowerCase().includes('lng') ||
                        key.toLowerCase().includes('lon')
                    );

                    if (gpsKeys.length > 0) {
                        // Try to extract coordinates from any latitude/longitude fields found
                        let foundLat = null;
                        let foundLng = null;

                        gpsKeys.forEach(key => {
                            const value = allExifData[key];
                            const keyLower = key.toLowerCase();

                            if (keyLower.includes('lat') && typeof value === 'number' && Math.abs(value) <= 90) {
                                foundLat = value;
                            }
                            if ((keyLower.includes('lng') || keyLower.includes('lon')) && typeof value === 'number' && Math.abs(value) <= 180) {
                                foundLng = value;
                            }
                        });

                        if (foundLat !== null && foundLng !== null) {
                            gpsCoords = {
                                lat: foundLat,
                                lng: foundLng,
                                source: 'exif_comprehensive_search'
                            };
                            foundGPS = true;
                        }
                    }
                }

                // Extract direction information and add to GPS coordinates
                let direction = null;
                if (foundGPS) {
                    // Extract direction from various EXIF sources
                    if (exifData?.GPSImgDirection !== undefined) {
                        direction = exifData.GPSImgDirection;
                    } else if (allExifData?.GPSImgDirection !== undefined) {
                        direction = allExifData.GPSImgDirection;
                    } else if (exifData?.GPSDestBearing !== undefined) {
                        direction = exifData.GPSDestBearing;
                    } else if (allExifData?.GPSDestBearing !== undefined) {
                        direction = allExifData.GPSDestBearing;
                    } else if (exifData?.GPSTrack !== undefined) {
                        direction = exifData.GPSTrack;
                    } else if (allExifData?.GPSTrack !== undefined) {
                        direction = allExifData.GPSTrack;
                    }

                    // Add direction to GPS coordinates if found
                    if (direction !== null && gpsCoords) {
                        (gpsCoords as any).direction = direction;
                    }

                    // Extract timestamp information
                    let takenAt = null;
                    if (exifData && (exifData.DateTimeOriginal || exifData.CreateDate || exifData.DateTime)) {
                        const timestamp = exifData.DateTimeOriginal || exifData.CreateDate || exifData.DateTime;
                        takenAt = new Date(timestamp).toISOString();
                    } else if (allExifData && (allExifData.DateTimeOriginal || allExifData.CreateDate || allExifData.DateTime)) {
                        const timestamp = allExifData.DateTimeOriginal || allExifData.CreateDate || allExifData.DateTime;
                        takenAt = new Date(timestamp).toISOString();
                    }

                    // Add timestamp to GPS coordinates if found
                    if (takenAt && gpsCoords) {
                        (gpsCoords as any).takenAt = takenAt;
                    }

                    // Log other GPS info if available
                    if (exifData?.GPSAltitude || allExifData?.GPSAltitude) {
                        const altitude = exifData?.GPSAltitude || allExifData?.GPSAltitude;
                        if (gpsCoords) {
                            (gpsCoords as any).altitude = altitude;
                        }
                    }
                }
            }

            // Fallback to Geolocation API coordinates if no EXIF GPS data
            if (!gpsCoords && locationCoords) {
                gpsCoords = {
                    lat: locationCoords.lat,
                    lng: locationCoords.lng,
                    accuracy: locationCoords.accuracy,
                    timestamp: locationCoords.timestamp,
                    source: 'geolocation_api',
                    // Add current timestamp as takenAt for Geolocation API captures
                    takenAt: new Date().toISOString()
                };
            }

        } catch (error) {
            console.warn('Failed to extract EXIF GPS data:', error);
        }

        const newImage = {
            id: `img_${Date.now()}`,
            type: 'upload' as const,
            name: processedFile.name,
            original_file_name: processedFile.name,
            thumbnail: imageUrl,
            file: processedFile,
            gpsCoords: gpsCoords, // Store GPS coordinates for WFS-T
            exifData: exifData // Store full EXIF data if available
        };

        setImageItems(prev => [...prev, newImage]);
    };

    // Media handling functions
    const handleImageUpload = () => {
        // For web, go directly to gallery. For mobile/tablet, show popup
        if (isDesktop) {
            handleImageFromGallery();
        } else {
            setCurrentMediaType('image');
            setMediaPopupOpen(true);
        }
    };

    // iOS Safari: click must be synchronous (no await before it), and the input must be
    // appended to the DOM so iOS doesn't garbage-collect it when the camera app opens.
    const handleImageCapture = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.png,.jpg,.jpeg,.heic,.heif';
        fileInput.capture = 'environment'; // Use rear camera
        fileInput.multiple = false;
        fileInput.style.display = 'none';
        document.body.appendChild(fileInput);

        fileInput.onchange = async (event: Event) => {
            document.body.removeChild(fileInput);
            const target = event.target as HTMLInputElement;
            const file = target.files?.[0];

            if (file) {
                // Validate file size first
                if (!validateFileSize(file)) {
                    return;
                }

                // Validate image format (extension or MIME type) before processing
                const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
                const mime = file.type;
                if (!SUPPORTED_IMAGE_EXTS.includes(ext) && !SUPPORTED_IMAGE_MIME_TYPES.includes(mime)) {
                    showAlert(t('observation.imageFormatError'), 'error');
                    return;
                }

                // Get GPS after capture so the click above stays synchronous
                const currentGPS = await getLocationForObservation();
                await addImageWithExifAndLocationData(file, currentGPS);
            }
        };

        fileInput.click();
    };

    const handleImageFromGallery = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.png,.jpg,.jpeg,.heic,.heif';
        fileInput.multiple = false;
        fileInput.style.display = 'none';
        document.body.appendChild(fileInput);

        fileInput.onchange = async (event: Event) => {
            document.body.removeChild(fileInput);
            const target = event.target as HTMLInputElement;
            const file = target.files?.[0];

            if (file) {
                // Validate file size first
                if (!validateFileSize(file)) {
                    return;
                }

                // Validate image format (extension or MIME type) before processing
                const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
                const mime = file.type;
                if (!SUPPORTED_IMAGE_EXTS.includes(ext) && !SUPPORTED_IMAGE_MIME_TYPES.includes(mime)) {
                    showAlert(t('observation.imageFormatError'), 'error');
                    return;
                }

                await addImageWithExifData(file);
            }
        };

        fileInput.click();
    };

    const handleVideoUpload = () => {
        // For web, go directly to gallery. For mobile/tablet, show popup
        if (isDesktop) {
            handleVideoFromGallery();
        } else {
            setCurrentMediaType('video');
            setMediaPopupOpen(true);
        }
    };

    const SUPPORTED_VIDEO_FORMATS = ['.mp4', '.mov', '.mkv', '.hevc'];
    const SUPPORTED_VIDEO_MIME_TYPES = [
        'video/mp4',
        'video/quicktime',
        'video/x-matroska',
    ];
    // Image support - include HEIC/HEIF for iPhones
    const SUPPORTED_IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.heic', '.heif'];
    const SUPPORTED_IMAGE_MIME_TYPES = [
        'image/jpeg',
        'image/png',
        'image/heic',
        'image/heif',
    ];
    const handleVideoCapture = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'video/mp4,video/quicktime,video/x-matroska,.mp4,.mov,.mkv,.hevc';
        fileInput.capture = 'environment'; // Use rear camera
        fileInput.multiple = false;
        fileInput.style.display = 'none';
        document.body.appendChild(fileInput);

        fileInput.onchange = async (event: Event) => {
            document.body.removeChild(fileInput);
            const target = event.target as HTMLInputElement;
            const file = target.files?.[0];

            if (file) {
                // Validate file size first
                if (!validateFileSize(file)) {
                    return;
                }

                // Validate video format (extension and MIME type)
                const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
                const mime = file.type;
                if (!SUPPORTED_VIDEO_FORMATS.includes(ext) && !SUPPORTED_VIDEO_MIME_TYPES.includes(mime)) {
                    showAlert(t('observation.videoFormatError'), 'error');
                    return;
                }
                try {
                    // Generate thumbnail from raw video (no compression)
                    const thumbnailData = await generateVideoThumbnail(file);
                    // Create object URL for immediate playback
                    const videoUrl = URL.createObjectURL(file);
                    // Add the raw video to state with generated thumbnail
                    const newVideo = {
                        id: `vid_${Date.now()}`,
                        type: 'capture' as const,
                        name: file.name,
                        original_file_name: file.name,
                        thumbnail: thumbnailData.url,
                        thumbnailFile: thumbnailData.file,
                        videoUrl: videoUrl,
                        file: file,
                        isHevc: thumbnailData.isHevc
                    };
                    setVideoItems(prev => [...prev, newVideo]);

                    // Show info message for HEVC videos
                    if (thumbnailData.isHevc) {
                        showAlert(t('observation.hevcVideo'), 'info');
                    }
                } catch (error) {
                    console.error('Video thumbnail error:', error);
                    showAlert(t('observation.videoProcessingFailed'), 'error');
                }
            }
        };

        fileInput.click();
    };

    const handleVideoFromGallery = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'video/mp4,video/quicktime,video/x-matroska,.mp4,.mov,.mkv,.hevc';
        fileInput.multiple = false;
        fileInput.style.display = 'none';
        document.body.appendChild(fileInput);

        fileInput.onchange = async (event: Event) => {
            document.body.removeChild(fileInput);
            const target = event.target as HTMLInputElement;
            const file = target.files?.[0];

            if (file) {
                // Validate file size first
                if (!validateFileSize(file)) {
                    return;
                }

                // Validate video format (extension and MIME type)
                const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
                const mime = file.type;
                if (!SUPPORTED_VIDEO_FORMATS.includes(ext) && !SUPPORTED_VIDEO_MIME_TYPES.includes(mime)) {
                    showAlert(t('observation.videoFormatError'), 'error');
                    return;
                }
                try {
                    // Generate thumbnail from raw video
                    const thumbnailData = await generateVideoThumbnail(file);
                    // Create object URL for immediate playback
                    const videoUrl = URL.createObjectURL(file);
                    // Add the raw video to state with generated thumbnail
                    const newVideo = {
                        id: `vid_${Date.now()}`,
                        type: 'upload' as const,
                        name: file.name,
                        original_file_name: file.name,
                        thumbnail: thumbnailData.url,
                        thumbnailFile: thumbnailData.file,
                        videoUrl: videoUrl,
                        file: file,
                        isHevc: thumbnailData.isHevc
                    };
                    setVideoItems(prev => [...prev, newVideo]);

                    // Show info message for HEVC videos
                    if (thumbnailData.isHevc) {
                        showAlert(t('observation.hevcVideo'), 'info');
                    }
                } catch (error) {
                    showAlert(t('observation.videoProcessingFailed'), 'error');
                }
            }
        };

        fileInput.click();
    };

    const handleAudioCapture = () => {
        setAudioRecorderOpen(true);
    };

    const handleAudioUpload = () => {
        setCurrentMediaType('audio');
        setMediaPopupOpen(true);
    };

    // Audio save: just add file, no compression
    const handleAudioSave = async (audioBlob: Blob, duration: number) => {
        // Detect extension from blob type
        let extension = 'wav';
        if (audioBlob.type === 'audio/webm') extension = 'webm';
        else if (audioBlob.type === 'audio/mp3') extension = 'mp3';
        else if (audioBlob.type === 'audio/mpeg') extension = 'mp3';
        else if (audioBlob.type === 'audio/x-m4a' || audioBlob.type === 'audio/m4a') extension = 'm4a';

        // Wrap blob in a File object with correct name and extension
        const audioFile = new File([audioBlob], `recorded_audio_${audioItems.length + 1}.${extension}`, { type: audioBlob.type });

        // Create object URLs for display and playback
        const audioUrl = URL.createObjectURL(audioFile);
        const newAudio = {
            id: `aud_${Date.now()}`,
            type: 'upload' as const,
            name: audioFile.name,
            original_file_name: audioFile.name,
            thumbnail: audioUrl,
            audioUrl: audioUrl,
            file: audioFile,
            duration: duration,
        };

        setAudioItems(prev => [...prev, newAudio]);
        setAudioRecorderOpen(false);
    };

    // Media preview handlers
    const handleImagePreview = (item: any) => {
        setPreviewMediaItem(item);
        setPreviewMediaType('image');
        setPreviewModalOpen(true);
    };

    const handleVideoPreview = (item: any) => {
        setPreviewMediaItem(item);
        setPreviewMediaType('video');
        setPreviewModalOpen(true);
    };

    const handleAudioPreview = (item: any) => {
        setPreviewMediaItem(item);
        setPreviewMediaType('audio');
        setPreviewModalOpen(true);
    };

    const handlePreviewClose = () => {
        setPreviewModalOpen(false);
        setPreviewMediaItem(null);
    };

    // Media remove handlers with advanced photo management
    const handleImageRemove = (id: string) => {
        setImageItems(prev => prev.map(item => {
            if (item.id === id) {
                if (item.file) {
                    // New photo: Remove completely from array
                    return null;
                } else {
                    // Existing photo: Mark for deletion but keep in array for tracking
                    return {
                        ...item,
                        toDelete: true,
                        thumbnail: item.thumbnail + '?deleted=true' // Visual indicator (optional)
                    };
                }
            }
            return item;
        }).filter(item => item !== null)); // Remove null items (new photos)
    };

    const handleVideoRemove = (id: string) => {
        setVideoItems(prev => prev.map(item => {
            if (item.id === id) {
                if (item.file) {
                    // New video: Remove completely
                    return null;
                } else {
                    // Existing video: Mark for deletion
                    return {
                        ...item,
                        toDelete: true,
                        thumbnail: item.thumbnail + '?deleted=true'
                    };
                }
            }
            return item;
        }).filter(item => item !== null));
    };

    const handleAudioRemove = (id: string) => {
        setAudioItems(prev => prev.map(item => {
            if (item.id === id) {
                if (item.file) {
                    // New audio: Remove completely
                    return null;
                } else {
                    // Existing audio: Mark for deletion
                    return {
                        ...item,
                        toDelete: true,
                        thumbnail: item.thumbnail + '?deleted=true'
                    };
                }
            }
            return item;
        }).filter(item => item !== null));
    };

    // Media popup handlers
    const handleMediaPopupCapture = () => {
        if (currentMediaType === 'image') {
            handleImageCapture();
        } else if (currentMediaType === 'video') {
            handleVideoCapture();
        } else if (currentMediaType === 'audio') {
            handleAudioCapture();
        }
    };

    const handleMediaPopupLibrary = () => {
        if (currentMediaType === 'image') {
            handleImageFromGallery();
        } else if (currentMediaType === 'video') {
            handleVideoFromGallery();
        } else if (currentMediaType === 'audio') {
            // Create file input for audio selection
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'audio/mpeg,audio/mp4,audio/aac,audio/ogg,audio/wav,.mp3,.m4a,.aac,.ogg,.wav';
            input.onchange = async (event) => {
                const file = (event.target as HTMLInputElement).files?.[0];
                if (file) {
                    // Validate file size first
                    if (!validateFileSize(file)) {
                        return;
                    }

                    // Validate audio format (extension and MIME type)
                    const SUPPORTED_AUDIO_FORMATS = ['.mp3', '.m4a', '.aac', '.ogg', '.wav'];
                    const SUPPORTED_AUDIO_MIME_TYPES = [
                        'audio/mpeg',
                        'audio/mp3',
                        'audio/x-m4a',
                        'audio/m4a',
                        'audio/aac',
                        'audio/ogg',
                        'audio/wav',
                    ];
                    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
                    const mime = file.type;
                    if (!SUPPORTED_AUDIO_FORMATS.includes(ext) && !SUPPORTED_AUDIO_MIME_TYPES.includes(mime)) {
                        showAlert(t('observation.audioFormatError'), 'error');
                        return;
                    }
                    // Create object URLs for display and playback
                    const audioUrl = URL.createObjectURL(file);
                    const newAudio = {
                        id: `aud_${Date.now()}`,
                        type: 'upload' as const,
                        name: file.name,
                        original_file_name: file.name,
                        thumbnail: audioUrl,
                        audioUrl: audioUrl,
                        file: file,
                    };
                    setAudioItems(prev => [...prev, newAudio]);
                }
            };
            input.click();
        }
    };

    // New WFS-T submission function
    const onSubmitWFS = async (data: ObservationData) => {
        if (isSaving) return; // Prevent multiple submissions
        setIsSaving(true);
        try {
            // Get authentication details
            let authUsername = LocalStorageUtil.getItem(storageKeys.emailId, "");
            let authToken = LocalStorageUtil.getItem(storageKeys.authToken, "");

            // Get dynamic parameters from localStorage and state
            const taskId = Number(LocalStorageUtil.getItem(storageKeys.taskId, 0));

            // Try to get projectId and layerId from stored project data or use defaults
            let projectId = Number(LocalStorageUtil.getItem('selectedProjectIdForNavigation', 1));
            let layerId: number | undefined = undefined;
            try {
                const storedProjectData = localStorage.getItem('currentProjectData');
                if (storedProjectData) {
                    const projectData = JSON.parse(storedProjectData);
                    // Use project_id as the primary project identifier for WFS-T
                    projectId = Number(projectData.project_id) || projectId;
                    if (projectData.layer_id !== undefined && projectData.layer_id !== null) {
                        layerId = Number(projectData.layer_id);
                    } else {
                        layerId = undefined;
                    }
                } else {
                    layerId = undefined;
                }
            } catch (e) {
                layerId = undefined;
                console.warn('Failed to parse project data:', { projectId, layerId, error: e });
            }

            // Transform form data to backend format with additional WFS-T fields
            const companyId = import.meta.env.VITE_APP_COMPANY_ID;
            const observation: Record<string, any> = {
                species_id: Number(data.species_id) || 0,
                layer_id: layerId,
                company_id: companyId,
                tenant_id: companyId,
                date: data.date,
                time: data.time + ':00Z', // Add timezone format like in GML examples
                observer: data.observer,
                mapnr: data.mapnr,
                not_sure: Boolean(data.not_sure || false),
                quantity: data.quantity,
                count_method_id: Number(data.count_method_id) || 0,
                ndff_stage_id: Number(data.ndff_stage_id) || 0,
                behaviour_id: Number(data.behaviour_id) || 0,
                trace_id: Number(data.trace_id) || 0,
                residence_id: Number(data.residence_id) || 0,
                residence_loc_id: Number(data.residence_loc_id) || 0,
                remark: data.remark,
                ndff_gender_id: Number(data.ndff_gender_id) || 0,
                ndff_determined: data.ndff_determined,
                ndff_counted: data.ndff_counted,
                ndff_cod_id: Number(data.ndff_cod_id) || 0,
                project_id: projectId,
                task_id: taskId,
                species_group_id: selectedSpecies?.species_group_id || editObservationData?.species_group_id || null,
                species_subgroup_id: selectedSpecies?.species_subgroup_id || editObservationData?.species_subgroup_id || null,
                api_temperature: data.api_temperature,
                api_weather_code: data.api_weather_code,
                api_wind_direction: data.api_wind_direction,
                api_wind_speed: data.api_wind_speed,
                manual_temperature: data.manual_temperature,
                manual_weather_code: data.manual_weather_code,
                manual_wind_direction: data.manual_wind_direction,
                manual_wind_speed: data.manual_wind_speed
            };

            console.log("observation data", observation)
            let speciesGroupId = selectedSpecies?.species_group_id || editObservationData?.species_group_id || null;
            let speciesSubgroupId = selectedSpecies?.species_subgroup_id || editObservationData?.species_subgroup_id || null;

            // Get species_subgroup_id from the actual selected species in dropdown data
            if (currentDropdownData && data.species_id) {
                const foundSpecies = currentDropdownData.data?.find((group: any) =>
                    group.species?.some((species: any) => species.species_id === Number(data.species_id))
                );
                if (foundSpecies) {
                    const selectedSpeciesData = foundSpecies.species?.find((species: any) =>
                        species.species_id === Number(data.species_id)
                    );
                    if (selectedSpeciesData && selectedSpeciesData.species_subgroup_id) {
                        speciesSubgroupId = selectedSpeciesData.species_subgroup_id;
                    }
                }
            }

            // Update observation object with resolved species group data
            observation.species_group_id = speciesGroupId;
            observation.species_subgroup_id = speciesSubgroupId;

            let response: any = undefined;
            if (observationId) {

                // Edit mode: update observation via WFS-T
                try {
                    const auth = {
                        authUsername: authUsername,
                        authToken: authToken,
                        customerId: companyId
                    };

                    let updateGeometry = false;
                    let geometry: { lng: number; lat: number } | undefined = undefined;
                    if (mapLocation) {
                        updateGeometry = true;
                        geometry = { lng: mapLocation.lng, lat: mapLocation.lat };
                    } else {
                        // If you want to allow manual geometry update, add logic here
                        updateGeometry = false;
                        geometry = undefined;
                    }

                    // Validate that photo and media items are properly formatted before submission
                    try {
                        // Check for any new photos that don't have valid files
                        const invalidPhotos = imageItems.filter(item =>
                            item.file && (!item.file.name || item.file.size === 0)
                        );
                        if (invalidPhotos.length > 0) {
                            showAlert(t('photoFilesInvalid'), 'error');
                            return;
                        }

                        // Check for any new media (video/audio) that don't have valid files
                        const invalidMedia = [...videoItems, ...audioItems].filter(item =>
                            item.file && (!item.file.name || item.file.size === 0)
                        );
                        if (invalidMedia.length > 0) {
                            showAlert(t('mediaFilesInvalid'), 'error');
                            return;
                        }
                    } catch (validationError) {
                        console.error('Error validating media files:', validationError);
                        showAlert(t('mediaValidationError'), 'error');
                        return;
                    }

                    // Convert imageItems to PhotoItem[] format for advanced photo management
                    const photoItems = imageItems.map(item => {
                        if (item.file) {
                            // New photo (has file)
                            return {
                                isExisting: false as const,
                                file: item.file,
                                gpsCoords: item.gpsCoords
                            };
                        } else {
                            // Existing photo (no file, loaded from database)
                            return {
                                isExisting: true as const,
                                photo_id: item.photo_id,
                                url: item.url,
                                file_path: item.file_path,
                                file_name: item.file_name,
                                obs_species_id: item.obs_species_id,
                                project_id: item.project_id,
                                direction: item.direction,
                                taken_at: item.taken_at,
                                toDelete: item.toDelete || false
                            };
                        }
                    });

                    // No video compression: just use original files
                    const processedVideoItems = videoItems;

                    // Convert mediaItems to MediaItem[] format for advanced media management
                    const mediaItems = [...audioItems, ...processedVideoItems].map(item => {
                        if (item.file) {
                            // New media (has file)
                            const isVideo = processedVideoItems.includes(item);
                            return {
                                isExisting: false as const,
                                file: item.file,
                                type: (audioItems.includes(item) ? 'audio' : 'video') as 'audio' | 'video',
                                ...(isVideo && item.thumbnailFile ? { thumbnail: item.thumbnailFile } : {})
                            };
                        } else {
                            // Existing media (no file, loaded from database)
                            return {
                                isExisting: true as const,
                                media_id: item.media_id,
                                file_path: item.file_path,
                                file_name: item.file_name,
                                obs_species_id: item.obs_species_id,
                                type: item.type as 'audio' | 'video',
                                taken_at: item.taken_at,
                                toDelete: item.toDelete || false
                            };
                        }
                    });

                    response = await observationApiService.updateObservationWFS(
                        observationId,
                        observation,
                        updateGeometry ? geometry! : undefined,
                        projectId,
                        taskId,
                        layerId,
                        photoItems,
                        mediaItems,
                        [],
                        auth,
                    );

                    // Check if response indicates failure (status_code 0 or explicit media errors)
                    if (response && response.status_code === 0 && response.status_message) {
                        showAlert(t('observation.saveFailed', { message: response.status_message }), 'error');
                        setIsSaving(false);
                        return;
                    }

                    // Check if media upload failed in the response
                    if (response && response.media_errors && Object.keys(response.media_errors).length > 0) {
                        const mediaErrorMessages = Object.entries(response.media_errors)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join('\n');
                        showAlert(t('observation.mediaUploadFailedUpdate', { details: mediaErrorMessages }), 'error');
                        setIsSaving(false);
                        return;
                    }

                } catch (wfsError: any) {
                    const errorMessage = wfsError?.message || 'Unknown WFS-T error';
                    showAlert(`Observation update failed: ${errorMessage}`, 'error');
                    setIsSaving(false);
                    return;
                }
            } else {
                // Create mode: save new observation via WFS-T
                if (mapLocation) {
                    try {
                        const auth = {
                            authUsername: authUsername,
                            authToken: authToken,
                            customerId: companyId
                        };

                        // Convert imageItems to PhotoItem[] format for create mode
                        const photoItems = imageItems.map(item => ({
                            isExisting: false as const,
                            file: item.file,
                            gpsCoords: item.gpsCoords
                        }));

                        // Convert mediaItems to MediaItem[] format (all new for create mode)
                        const newMediaItems = [...audioItems, ...videoItems].map(item => {
                            const isVideo = videoItems.includes(item);
                            return {
                                isExisting: false as const,
                                file: item.file,
                                type: audioItems.includes(item) ? 'audio' as const : 'video' as const,
                                ...(isVideo && item.thumbnailFile ? { thumbnail: item.thumbnailFile } : {})
                            };
                        });

                        response = await observationApiService.saveObservationWFS(
                            observation,
                            { lng: mapLocation.lng, lat: mapLocation.lat },
                            projectId,
                            taskId,
                            layerId,
                            photoItems, // Pass PhotoItem[]
                            newMediaItems, // Pass MediaItem[] with proper typing
                            [], // mediaTypes - no longer needed, type info is in MediaItem[]
                            auth,
                        );

                        // Check if response indicates failure
                        if (response && response.status_code === 0 && response.status_message) {
                            showAlert(t('observation.saveFailed', { message: response.status_message }), 'error');
                            setIsSaving(false);
                            return;
                        }

                        // Check if media upload failed in the response
                        if (response && response.media_errors && Object.keys(response.media_errors).length > 0) {
                            const mediaErrorMessages = Object.entries(response.media_errors)
                                .map(([key, value]) => `${key}: ${value}`)
                                .join('\n');
                            showAlert(t('observation.mediaUploadFailedSave', { details: mediaErrorMessages }), 'error');
                            setIsSaving(false);
                            return;
                        }

                    } catch (wfsError: any) {
                        const errorMessage = wfsError?.message || 'Unknown WFS-T error';
                        showAlert(t('observation.saveFailed', { message: errorMessage }), 'error');
                        setIsSaving(false);
                        return;
                    }
                } else {
                    showAlert(t('observation.noMapLocation'), 'error');
                    return;
                }
            }

            if (response && (response.status_code === 1 || response.status === true)) {
                // For new observations, store the location address on the server (fire-and-forget)
                if (!observationId && response.obs_species_id && mapLocation) {
                    try {
                        await observationApiService.storeLocationAddress(
                            response.obs_species_id,
                            mapLocation.lat,
                            mapLocation.lng
                        );
                    } catch (locationErr) {
                        console.error('Failed to store location address:', locationErr);
                        // Non-critical – don’t block navigation
                    }
                }

                let messageKey = '';
                if (response.offline) {
                    messageKey = observationId ? 'observation.updateOffline' : 'observation.saveOffline';
                } else {
                    messageKey = observationId ? 'observation.updateSuccess' : 'observation.saveSuccess';
                }

                // Navigate then show success message
                setTimeout(() => {
                    navigate(-1);
                    // Show alert after a brief delay to ensure navigation has started
                    setTimeout(() => {
                        showAlert(t(messageKey), response.offline ? 'warning' : 'success');
                    }, 300);
                }, 1500); // 1.5 second delay before navigation

            } else {
                showAlert(t('observation.syncError'), 'error');
                navigate(-1);
                setIsSaving(false);
            }
        } catch (error) {
            console.error('Failed to save observation via WFS-T:', error);
            showAlert(t('common.server_error', 'An error occurred while saving the observation'), 'error');
            // } finally {
            setIsSaving(false);
        }
    };

    if (!selectedSpecies && !observationId) {
        return null;
    }

    return (
        <Container
            maxWidth={false}
            disableGutters={isMobile}
            sx={{
                minHeight: '100vh',
                bgcolor: 'background.default',
                p: isMobile ? 0 : 3,
                pb: 10,
                width: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
                position: 'relative',
            }}
        >
            {/* Full-screen loader when saving or editing */}
            {(isSaving) && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999,
                    }}
                >
                    <CommonLoader isVisible={true} />
                </Box>
            )}

            {editLoading && observationId ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <CommonLoader isVisible={true} />
                </Box>
            ) : (
                <>
                    <Stack spacing={isMobile ? 1 : 2} sx={{ width: '100%', boxSizing: 'border-box', overflow: 'hidden', px: 0, mx: 0, pt: isMobile ? 1.5 : 0 }}>
                        {/* Header */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                            }}
                        >
                            <IconButton
                                onClick={handleBack}
                                sx={{
                                    color: brandMain,
                                }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography
                                variant={isMobile ? 'h6' : 'h5'}
                                sx={{
                                    fontFamily: "Nunito Sans Regular700",
                                    // fontWeight: 600,
                                    color: brandMain,
                                }}
                            >
                                Waarneming {
                                    (() => {
                                        // Show subcategory for 'others' in edit mode
                                        if (observationId && editObservationData && faunaSpeciesGroups.length > 0) {
                                            const others = faunaSpeciesGroups.find(g => g.id === 'others');
                                            if (others && others.groups && editObservationData.species_group_id) {
                                                const group = others.groups.find((gr: any) => gr.species_group_id === editObservationData.species_group_id);
                                                if (group) return `- ${group.name}`;
                                            }
                                        }
                                        // Else use selectedSpecies subcategory
                                        return selectedSpecies?.subcategory ? `- ${selectedSpecies.subcategory}` : '';
                                    })()
                                }
                            </Typography>
                            {/* Species icon after title */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 32,
                                    height: 32,
                                    borderRadius: 1,
                                    bgcolor: brandMain,
                                    p: 0.5,
                                }}
                            >
                                {renderSpeciesIconInverted(getSpeciesIconId(), 20, 20)}
                            </Box>
                        </Box>

                        {/* Network Status Indicator */}
                        {!isOnline && (
                            <Alert
                                severity="warning"
                                sx={{
                                    mb: 2,
                                    '& .MuiAlert-message': {
                                        fontFamily: theme.typography.fontFamily,
                                    }
                                }}
                            >
                                {t('offline.working_offline')}
                            </Alert>
                        )}

                        {/* Main Form */}
                        <Box
                            sx={{
                                bgcolor: 'background.paper',
                                borderRadius: isMobile ? 0 : 2,
                                overflow: 'hidden',
                                width: '100%',
                                boxSizing: 'border-box',
                                mx: 0,
                            }}
                        >
                            {/* Basic Information */}
                            <Box sx={{ p: isMobile ? 1 : 2, width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                                <ObservationForm
                                    control={control}
                                    errors={errors}
                                    isMobile={isMobile}
                                    latitude={mapLocation?.lat}
                                    longitude={mapLocation?.lng}
                                    geom={editObservationData?.geom}
                                    editModeAddress={observationId ? (editObservationData?.address ?? '') : undefined}
                                    onAddressUpdate={(address: string, buildingInfo?: BuildingInfo) => {
                                        setValue('address', address);
                                        if (buildingInfo) {
                                            setLocationBuildingInfo(buildingInfo);
                                        }
                                    }}
                                    dropdownData={currentDropdownData}
                                    speciesGroupId={selectedSpecies?.species_group_id || editObservationData?.species_group_id}
                                    speciesSubgroupId={selectedSpecies?.species_subgroup_id || editObservationData?.species_subgroup_id}
                                    isEditMode={!!observationId}
                                    observerName={editObservationData?.observer_name || ''}
                                    apiWeatherData={apiWeatherData || undefined}
                                    manualWeatherData={manualWeatherData || undefined}
                                    onManualWeatherChange={handleManualWeatherChange}
                                    isOnline={isOnline}
                                />
                            </Box>

                            {/* Soortinformatie Accordion */}
                            <Accordion
                                expanded={soortinformatieExpanded}
                                onChange={handleSoortinformatieToggle}
                                sx={{
                                    '&:before': {
                                        display: 'none',
                                    },
                                    boxShadow: 'none',
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={
                                        soortinformatieExpanded ? (
                                            <CollapseIcon style={{ width: '50px', height: '50px', color: brandMain }} />
                                        ) : (
                                            <ExpandIcon style={{ width: '50px', height: '50px', color: brandMain }} />
                                        )
                                    }
                                    sx={{
                                        bgcolor: 'white',
                                        '& .MuiAccordionSummary-expandIconWrapper': {
                                            order: 1,
                                            marginLeft: 'auto',
                                            marginRight: 0,
                                        },
                                        '& .MuiAccordionSummary-content': {
                                            order: 0,
                                            marginRight: 'auto',
                                            margin: '12px 0',
                                        },
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontFamily: "Nunito Sans Regular700",
                                            fontWeight: 600,
                                            color: brandMain,
                                        }}
                                    >
                                        Soortinformatie
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: isMobile ? 2 : 3 }}>
                                    <ObservationForm
                                        control={control}
                                        errors={errors}
                                        isMobile={isMobile}
                                        showExtendedFields={true}
                                        latitude={mapLocation?.lat}
                                        longitude={mapLocation?.lng}
                                        editModeAddress={editObservationData?.address}
                                        geom={editObservationData?.geom}
                                        onAddressUpdate={(address: string) => {
                                            setValue('address', address);
                                        }}
                                        dropdownData={currentDropdownData}
                                        speciesGroupId={selectedSpecies?.species_group_id || editObservationData?.species_group_id}
                                        speciesSubgroupId={selectedSpecies?.species_subgroup_id || editObservationData?.species_subgroup_id}
                                        apiWeatherData={apiWeatherData || undefined}
                                        manualWeatherData={manualWeatherData || undefined}
                                        onManualWeatherChange={handleManualWeatherChange}
                                        isOnline={isOnline}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        </Box>

                        {/* Media Upload Sections */}
                        <Grid2 container spacing={isMobile ? 0 : 2} sx={{ width: '100%', boxSizing: 'border-box', overflow: 'hidden', margin: 0 }}>
                            <Grid2 size={{ xs: 12, md: 4 }}>
                                <MediaUploadSection
                                    title="Foto vastleggen / uploaden"
                                    mediaType="image"
                                    items={imageItems.filter(item => !item.toDelete)} // Hide photos marked for deletion
                                    onCapture={handleImageUpload}
                                    onUpload={handleImageUpload}
                                    onPreview={handleImagePreview}
                                    onRemove={handleImageRemove}
                                    maxItems={10}
                                />
                            </Grid2>

                            <Grid2 size={{ xs: 12, md: 4 }}>
                                <MediaUploadSection
                                    title="Video vastleggen / uploaden"
                                    mediaType="video"
                                    items={videoItems.filter(item => !item.toDelete)}
                                    onCapture={handleVideoUpload}
                                    onUpload={handleVideoUpload}
                                    onPreview={handleVideoPreview}
                                    onRemove={handleVideoRemove}
                                    maxItems={10}
                                />
                            </Grid2>

                            <Grid2 size={{ xs: 12, md: 4 }}>
                                <MediaUploadSection
                                    title="Audio uploaden / opnemen"
                                    mediaType="audio"
                                    items={audioItems.filter(item => !item.toDelete)}
                                    onCapture={handleAudioCapture}
                                    onUpload={handleAudioUpload}
                                    onPreview={handleAudioPreview}
                                    onRemove={handleAudioRemove}
                                    maxItems={10}
                                />
                            </Grid2>
                        </Grid2>

                        {/* Submit Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mb: 10 }}>
                            <CommonButton
                                translationKey={isSaving ? "common.saving" : (observationId ? "common.button_wijzig" : "common.button_opslaan")}
                                onClick={handleSubmit(onSubmitWFS)}
                                disabled={isSaving}
                                fullWidth={false}
                                sx={{
                                    fontFamily: "Nunito Sans Regular500",
                                    fontWeight: 700,
                                    width: "105px",
                                    backgroundColor: isSaving ? "grey.400" : "primary.main",
                                    color: theme.palette.secondary.main,
                                    ml: 2,
                                }}
                            />
                            <CommonButton
                                translationKey={"common.button_cancel"}
                                onClick={handleBack}
                                disabled={isSaving}
                                fullWidth={false}
                                sx={{
                                    fontFamily: theme.typography.fontFamily,
                                    fontWeight: 700,
                                    width: "105px",
                                    backgroundColor: isSaving ? "grey.400" : "error.dark",
                                    color: theme.palette.secondary.main,
                                }}
                            />
                        </Box>
                    </Stack>

                    {/* Media Selection Popup - For Image, Video, and Audio */}
                    <MediaSelectionPopup
                        open={mediaPopupOpen}
                        onClose={() => setMediaPopupOpen(false)}
                        mediaType={currentMediaType}
                        onCapture={handleMediaPopupCapture}
                        onLibrary={handleMediaPopupLibrary}
                    />

                    {/* Media Preview Modal */}
                    <MediaPreviewModal
                        open={previewModalOpen}
                        onClose={handlePreviewClose}
                        mediaItem={previewMediaItem}
                        mediaType={previewMediaType}
                    />

                    {/* Audio Recorder Modal */}
                    <AudioRecorderModal
                        open={audioRecorderOpen}
                        onClose={() => setAudioRecorderOpen(false)}
                        onSave={handleAudioSave}
                    />
                </>
            )}
        </Container>
    );
};

export default ObservationScreen;
